import { BoxGeometry, Group, Mesh, MeshLambertMaterial, type BufferGeometry, type Material } from 'three';
import { EN21, craneStructure } from '../../core/enemies/port-crane';

/**
 * EN-21 Grúa portuaria (GDD §8.3, issue #104) in low-poly primitives with flat shading: masts and beam where
 * the core has its structure, a cabin with the warning beacon and an arm that turns on its pivot. The hitbox
 * is the 75 % of the model: the mast is 2.0 u wide for a 1.5 u hitbox and the arm 0.8 u thick for circles of
 * 0.3 u. The arm angle comes from the core every frame.
 */
export interface PortCrane {
  group: Group;
  arm: Group;
  setBeacon(on: boolean): void;
  dispose(): void;
}

export const CRANE_COLORS = { steel: 0xd08a2c, beam: 0x6d6f73, cabin: 0x2f4a66, beaconOff: 0x5a4a1a, beaconOn: 0xffc23a } as const;
const VISUAL_SCALE = 1 / 0.75;

export function buildPortCrane(name: string, x: number, y: number): PortCrane {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };

  const group = new Group();
  group.name = name;
  const steel = paint(CRANE_COLORS.steel);
  // The structure, in coordinates relative to the beam centre.
  craneStructure(x, y).forEach((piece, index) => {
    const mesh = new Mesh(own(new BoxGeometry(piece.halfW * 2 * VISUAL_SCALE, piece.halfH * 2, 1.2)), index === 0 ? paint(CRANE_COLORS.beam) : steel);
    mesh.position.set(piece.x - x, piece.y - y, 0);
    group.add(mesh);
  });
  const cabin = new Mesh(own(new BoxGeometry(0.9, 0.7, 0.9)), paint(CRANE_COLORS.cabin));
  cabin.position.set(-0.9, 0.2, 0.3);
  const beaconMaterial = new MeshLambertMaterial({ color: CRANE_COLORS.beaconOff, emissive: 0x000000, flatShading: true });
  materials.push(beaconMaterial);
  const beacon = new Mesh(own(new BoxGeometry(0.25, 0.25, 0.25)), beaconMaterial);
  beacon.name = `${name}.beacon`;
  beacon.position.set(-0.9, 0.7, 0.3);
  group.add(cabin, beacon);

  // The arm hangs from its pivot (the origin) and points along +x; the group turns around z.
  const arm = new Group();
  arm.name = name.replace(/^enemies\.EN-21\.(\d+)$/, 'enemy:EN-21:$1:arm');
  const boom = new Mesh(own(new BoxGeometry(EN21.armLength, EN21.armRadius * 2 * VISUAL_SCALE, 0.5)), steel);
  boom.position.x = EN21.armLength / 2;
  arm.add(boom);
  arm.position.z = 0.7;
  group.add(arm);
  group.position.set(x, y, 0);

  return {
    group,
    arm,
    setBeacon(on) {
      beaconMaterial.color.setHex(on ? CRANE_COLORS.beaconOn : CRANE_COLORS.beaconOff);
      beaconMaterial.emissive.setHex(on ? CRANE_COLORS.beaconOn : 0x000000);
    },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
